import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import LockIcon from '@mui/icons-material/Lock'
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant'
import ReactCountryFlag from 'react-country-flag'
import {
  Box,
  Button as MuiButton,
  Card as MuiCard,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography
} from '@mui/material'
import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { translateText } from '../../../language'
import { useNavigate } from 'react-router-dom'
import { useThemeMode } from '../../../theme'
import { VotingControls } from '../voting/Controls'
import { COUNTRIES } from './countries'

const makeStyles = (palette) => ({
  leftCol: {
    background: palette.headerColor,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.25,
    py: 1,
    px: 0.5,
    minHeight: '100%'
  },
  leftLabel: { color: palette.paperColor, textAlign: 'center', lineHeight: 1.1, opacity: 0.8 },
  leftNumber: { color: palette.paperColor, textAlign: 'center', lineHeight: 1.2 },
  leftIcon: { color: palette.paperColor, fontSize: '1.4rem', opacity: 0.85, mt: 0.25 },
  rightCol: { display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' },
  bodyBox: { px: 1.5, pt: 1, pb: 0.5, flex: 1, position: 'relative' },
  ownerNick: { opacity: 0.6 },
  description: {
    mt: 0.5,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    '& p': { margin: '0 0 0.3em 0' },
    '& p:last-child': { margin: 0 },
    '& ul, & ol': { margin: '0 0 0.3em 1.2em', padding: 0 },
    '& li': { marginBottom: '0.1em' },
    '& strong': { color: palette.headerColor },
    '& a': { color: palette.headerColor }
  },
  badgesRow: { mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' },
  lockIcon: { opacity: 0.5 },
  votedChip: {
    backgroundColor: palette.headerColor,
    color: palette.paperColor,
    fontSize: '0.68rem',
    height: '18px',
    '& .MuiChip-label': { px: 0.75 }
  },
  votingBox: { px: 1.5, pb: 1 },
  ownTableNote: { opacity: 0.6, fontStyle: 'italic' },
  actionsBox: { px: 1.5, pb: 1, pt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' },
  editBtn: { borderRadius: '25px', color: palette.headerColor, borderColor: palette.headerColor },
  activateBtn: { borderRadius: '25px', color: 'success.main', borderColor: 'success.main', marginLeft: 'auto' },
  approveBtn: { borderRadius: '25px', color: 'success.main', borderColor: 'success.main', marginLeft: 'auto' },
  deactivateBtn: { borderRadius: '25px', color: 'error.main', borderColor: 'error.main', marginLeft: 'auto' },
  leftColCompact: {
    background: palette.headerColor,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.25,
    py: 0.75,
    px: 0.5,
    minHeight: '100%'
  },
  leftLabelCompact: { color: palette.paperColor, textAlign: 'center', lineHeight: 1.1, opacity: 0.8, fontSize: '0.6rem' },
  leftNumberCompact: { color: palette.paperColor, textAlign: 'center', lineHeight: 1.2, fontSize: '0.7rem', fontWeight: 700 },
  leftIconCompact: { color: palette.paperColor, fontSize: '1rem', opacity: 0.85, mt: 0.25 }
})

const CardLeftColumn = ({ tableNumber, styles, t, compact }) => (
  <Grid item xs={compact ? 2 : 3} sx={compact ? styles.leftColCompact : styles.leftCol}>
    <Typography variant="caption" sx={compact ? styles.leftLabelCompact : styles.leftLabel}>
      {t('snacks-vote-for')}
    </Typography>
    <Typography variant={compact ? 'caption' : 'subtitle2'} fontWeight={700} sx={compact ? styles.leftNumberCompact : styles.leftNumber}>
      {tableNumber != null ? `${t('snacks-table')} ${tableNumber}` : '—'}
    </Typography>
    <TableRestaurantIcon sx={compact ? styles.leftIconCompact : styles.leftIcon} />
  </Grid>
)

const VotedChips = ({ tableNumber, campaigns, votes, i18n, styles }) =>
  (votes[tableNumber] ?? []).map((campaignName) => {
    const campaign = campaigns.find((c) => c.name === campaignName)
    const label = campaign ? translateText(campaign.titles, i18n) : campaignName
    return <Chip key={campaignName} label={`✓ ${label}`} size="small" sx={styles.votedChip} />
  })

const CardBody = ({ registration, styles, t, i18n, campaigns, votes, hideVotedChips }) => {
  const { table_number, name, description, status, is_locked } = registration
  const countryEntry = registration.country
    ? COUNTRIES.find((c) => c.code === registration.country)
    : null

  return (
    <Box sx={styles.bodyBox}>
      {countryEntry && (
        <ReactCountryFlag
          countryCode={countryEntry.code}
          svg
          style={{ position: 'absolute', top: '4px', right: '4px', width: '1.8em', height: '1.8em' }}
          title={countryEntry.name}
        />
      )}
      <Typography variant="body2" fontWeight={600} sx={{ pr: countryEntry ? 3 : 0 }}>
        {name}
      </Typography>
      {description && (
        <Box sx={styles.description}>
          <ReactMarkdown>{description}</ReactMarkdown>
        </Box>
      )}
      <Box sx={styles.badgesRow}>
        {status === 'pending' && <Chip label={t('snacks-status-waiting')} color="warning" size="small" />}
        {status === 'disabled' && <Chip label={t('snacks-status-deactivated')} color="error" size="small" />}
        {is_locked && <LockIcon fontSize="small" sx={styles.lockIcon} />}
        {!hideVotedChips && <VotedChips tableNumber={table_number} campaigns={campaigns} votes={votes} i18n={i18n} styles={styles} />}
      </Box>
    </Box>
  )
}

const CardVotingSection = ({ registration, isOwn, isAdmin, campaigns, votes, deadline, styles, t, singleRow, votingNotStarted }) => {
  const ownTableNumber = useSelector((state) => state.snacks.ownRegistration?.table_number)
  const { table_number, status } = registration

  if (status !== 'active' || isOwn || isAdmin || campaigns.length === 0) return null

  const ownsThisTable = table_number != null && ownTableNumber === table_number

  if (ownsThisTable) {
    return (
      <Box sx={styles.votingBox}>
        <Typography variant="caption" sx={styles.ownTableNote}>
          {t('snacks-own-table-no-vote')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={styles.votingBox} onClick={(e) => e.stopPropagation()}>
      <VotingControls
        campaigns={campaigns}
        votes={votes}
        deadline={deadline}
        entityId={table_number}
        category="snacks_exchange"
        hideAvailable
        singleRow={singleRow}
        votingNotStarted={votingNotStarted}
      />
    </Box>
  )
}

const CardAdminActions = ({ registration, styles, onEdit, onApprove, onActivate, onDeactivate, t }) => {
  const { id, status, is_locked } = registration
  return (
    <Box sx={styles.actionsBox} onClick={(e) => e.stopPropagation()}>
      <MuiButton
        variant="outlined"
        size="small"
        startIcon={<EditIcon fontSize="small" />}
        onClick={() => onEdit && onEdit(registration)}
        sx={styles.editBtn}
      >
        {t('snacks-edit')}
      </MuiButton>
      {status === 'pending' && (
        <MuiButton
          variant="outlined"
          size="small"
          startIcon={<CheckCircleOutlineIcon fontSize="small" />}
          onClick={() => onApprove && onApprove(registration)}
          sx={styles.approveBtn}
        >
          {t('snacks-approve')}
        </MuiButton>
      )}
      {status === 'disabled' && !is_locked && (
        <MuiButton
          variant="outlined"
          size="small"
          startIcon={<CheckCircleOutlineIcon fontSize="small" />}
          onClick={() => onActivate && onActivate(id)}
          sx={styles.activateBtn}
        >
          {t('snacks-activate')}
        </MuiButton>
      )}
      {status === 'active' && (
        <MuiButton
          variant="outlined"
          size="small"
          startIcon={<DeleteIcon fontSize="small" />}
          onClick={onDeactivate}
          sx={styles.deactivateBtn}
        >
          {t('snacks-deactivate')}
        </MuiButton>
      )}
    </Box>
  )
}

const DeactivateDialog = ({ open, onClose, onConfirm, t }) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>{t('snacks-deactivate-confirm-title')}</DialogTitle>
    <DialogContent>
      <DialogContentText>{t('snacks-deactivate-confirm')}</DialogContentText>
    </DialogContent>
    <DialogActions>
      <MuiButton onClick={onClose}>{t('cancel')}</MuiButton>
      <MuiButton onClick={onConfirm} color="error" variant="contained">
        {t('snacks-deactivate')}
      </MuiButton>
    </DialogActions>
  </Dialog>
)

export const RegistrationCard = ({
  registration,
  isAdmin,
  isOwn,
  linkTo,
  showVoting,
  onEdit,
  onApprove,
  onDeactivate,
  onActivate,
  flat,
  compact,
  singleRowVoting,
  votingNotStarted
}) => {
  const { t, i18n } = useTranslation()
  const { palette } = useThemeMode()
  const navigate = useNavigate()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const votingData = useSelector((state) => state.voting)
  const campaigns = votingData?.snacks_exchange?.campaigns ?? []
  const votes = votingData?.snacks_exchange?.votes ?? {}
  const deadline = votingData?.snacks_exchange?.deadline

  const styles = makeStyles(palette)
  const { id } = registration

  const handleDeactivate = () => {
    setConfirmOpen(false)
    if (onDeactivate) onDeactivate(id)
  }

  const gridContent = singleRowVoting
    ? (
      <Grid container>
        <CardLeftColumn tableNumber={registration.table_number} styles={styles} t={t} compact={compact} />
        <Grid item xs={compact ? 10 : 9} sx={styles.rightCol}>
          <CardBody registration={registration} styles={styles} t={t} i18n={i18n} campaigns={campaigns} votes={votes} hideVotedChips={showVoting} />
        </Grid>
        {showVoting && (
          <Grid item xs={12}>
            <CardVotingSection
              registration={registration}
              isOwn={isOwn}
              isAdmin={isAdmin}
              campaigns={campaigns}
              votes={votes}
              deadline={deadline}
              styles={styles}
              t={t}
              singleRow
              votingNotStarted={votingNotStarted}
            />
          </Grid>
        )}
      </Grid>
      )
    : (
      <Grid container sx={{ minHeight: compact ? '60px' : '80px' }}>
        <CardLeftColumn tableNumber={registration.table_number} styles={styles} t={t} compact={compact} />
        <Grid item xs={compact ? 10 : 9} sx={styles.rightCol}>
          <CardBody registration={registration} styles={styles} t={t} i18n={i18n} campaigns={campaigns} votes={votes} hideVotedChips={showVoting} />
          {showVoting && (
            <CardVotingSection
              registration={registration}
              isOwn={isOwn}
              isAdmin={isAdmin}
              campaigns={campaigns}
              votes={votes}
              deadline={deadline}
              styles={styles}
              t={t}
              singleRow={false}
              votingNotStarted={votingNotStarted}
            />
          )}
          {isAdmin && (
            <CardAdminActions
              registration={registration}
              styles={styles}
              onEdit={onEdit}
              onApprove={onApprove}
              onActivate={onActivate}
              onDeactivate={() => setConfirmOpen(true)}
              t={t}
            />
          )}
        </Grid>
      </Grid>
      )

  const cardWrapper = flat
    ? (
        <Box sx={{ width: '100%' }}>
          {gridContent}
        </Box>
      )
    : (
        <MuiCard
          sx={{ width: '100%', marginBottom: '4px', cursor: linkTo ? 'pointer' : 'default' }}
          onClick={linkTo ? () => navigate(linkTo) : undefined}
        >
          {gridContent}
        </MuiCard>
      )

  return (
    <Box sx={{ width: '100%' }}>
      {cardWrapper}

      {isAdmin && (
        <DeactivateDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleDeactivate}
          t={t}
        />
      )}
    </Box>
  )
}
